import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { Upright } from "../../../../../http/border/types/uprights";
import { UprightCap } from "../../../../../http/border/types/uprightCaps";
import { useUprightsApi } from "../../../../../http/border/useUprights";
import { useUprightCapsApi } from "../../../../../http/border/useUprightCaps";
import { SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import { itemLabel } from "../../../../../utils";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import MultiCheckbox from "../../../../ui/MultiCheckbox";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect } from "../../../../ui/select/CustomSelect";

interface UprightCapFormBody {
  item: SearchAdminItem | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  uprightIds: string[];
}

interface UprightCapFormProps {
  uprightCap?: UprightCap;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClose: () => void;
}

const UprightCapForm: FC<UprightCapFormProps> = ({ uprightCap, isDisabled, isLoading, onClose }) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);
  const aborter = useRef(new AbortController());

  const itemsApi = useItemsApi();
  const uprightsApi = useUprightsApi();
  const uprightCapsApi = useUprightCapsApi();
  const uprights = uprightsApi.useAdminUprights({ limit: 100 }, aborter.current.signal);

  const defaultValues = {
    item: uprightCap?.item ?? null,
    order: uprightCap?.order ?? 50,
    visibility: uprightCap?.visibility ?? "hidden",
    uprightIds: uprightCap?.uprights.map((upright) => upright.item.id) ?? [],
  };

  return (
    <Form<UprightCapFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isLoading || isSubmitting}
      secondaryButton={
        <Button buttonProps={{ onClick: onClose }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={() => async (formData) => {
        setIsSubmitting(true);

        if (uprightCap?.item.id) {
          uprightCapsApi
            .updateAdminUprightCap(uprightCap.item.id, {
              order: formData.order!,
              visibility: formData.visibility,
              uprightIds: formData.uprightIds,
            })
            .then(() => onClose())
            .catch((err) => dispatch(showErrorNotification(i18n, err)))
            .finally(() => setIsSubmitting(false));
        } else {
          try {
            await uprightCapsApi.insertAdminUprightCap({
              itemId: formData.item?.id ?? "",
              order: formData.order!,
              visibility: formData.visibility,
              uprightIds: formData.uprightIds,
            });
            onClose();
          } catch (err) {
            dispatch(showErrorNotification(i18n, err));
          } finally {
            setIsSubmitting(false);
          }
        }
      }}
    >
      {({ control, setValue, reset, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [uprightCap]);

        const uprightIds = watch("uprightIds");

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                    {({ labelId }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                      // Fetch initial data on mount
                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultOptions);
                      }, []);

                      return (
                        <CustomAsyncSelect
                          {...field}
                          inputId={labelId}
                          defaultOptions={defaultOptions}
                          isDisabled={!!uprightCap || isDisabled}
                          loadOptions={loadOptions}
                          isClearable
                          getOptionLabel={itemLabel}
                          getOptionValue={(x) => x.id}
                          isInvalid={fieldState.invalid}
                          onChange={(value) => setValue("item", value ?? null)}
                        />
                      );
                    }}
                  </FormField>
                );
              }}
            />
            <FormField label={i18n.translation.uprights.plural}>
              <MultiCheckbox
                disabled={isDisabled || isLoading || isSubmitting}
                values={
                  uprights.data?.items.map((upright: Upright) => ({
                    id: upright.item.id,
                    title: itemLabel(upright.item),
                    selected: uprightIds.includes(upright.item.id),
                  })) ?? []
                }
                onUpdate={(id, checked) => {
                  setValue(
                    "uprightIds",
                    checked ? [...uprightIds, id] : uprightIds.filter((uprightId) => uprightId !== id),
                  );
                }}
              />
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default UprightCapForm;
